"use client";

import type { FC } from "react";
import { cn } from "utils";
import {
  buildRadarCurvePath,
  buildRadarDataVertices,
  polarToCartesian,
  RADAR_AXES,
  RADAR_CENTER_BADGE_GAP,
  RADAR_CHIP_GEOMETRY,
  RADAR_GEOMETRY,
  RADAR_LABEL_POSITIONS,
  RADAR_LEVELS,
  RADAR_PLAN_IMPACT_AXES,
  RADAR_PLAN_IMPACT_CURVE_OPTIONS,
  RADAR_PLAN_IMPACT_GEOMETRY,
  RADAR_PLAN_IMPACT_LABEL_POSITIONS,
} from "./radarGeometry";
import type { FitnessRadarChartProps } from "./types";
import { useRadarRevealAnimation } from "./useRadarRevealAnimation";

const DIMENSION_LABELS: Record<(typeof RADAR_AXES)[number]["code"], string> = {
  control: "Control",
  safety: "Safety",
  growth: "Growth",
};

export const FitnessRadarChart: FC<FitnessRadarChartProps> = ({
  total,
  control,
  safety,
  growth,
  previous,
  size = "onboarding-result",
  animate = false,
  className,
}) => {
  const isCompact = size === "compact";
  const isPlanImpact = size === "plan-impact";
  const isChip = size === "chip";
  const isInstant = isCompact || isPlanImpact || isChip;
  const reveal = useRadarRevealAnimation({
    total,
    control,
    safety,
    growth,
    enabled: animate && !isInstant,
  });

  const scores = { control, safety, growth };
  const scoreMap = { control, safety, growth };
  const axes = isPlanImpact ? RADAR_PLAN_IMPACT_AXES : RADAR_AXES;
  const { cx, cy, maxR, viewBox } = isChip
    ? RADAR_CHIP_GEOMETRY
    : isPlanImpact
      ? RADAR_PLAN_IMPACT_GEOMETRY
      : RADAR_GEOMETRY;
  const labelPositions = isPlanImpact
    ? RADAR_PLAN_IMPACT_LABEL_POSITIONS
    : RADAR_LABEL_POSITIONS;
  /** compact: bare shape; chip: vertices only (no center/labels/badge) */
  const showDataPoints = !isCompact;
  const showAxisLabels = !isCompact && !isChip;
  const showCenterDot = !isCompact && !isChip;
  const showCenterScoreBadge = !isCompact && !isPlanImpact && !isChip;
  /** 刻度仅全尺寸结果页展示；compact / plan-impact / chip 隐藏 */
  const showScaleLabels = !isCompact && !isPlanImpact && !isChip;

  const curveOptions = isPlanImpact
    ? RADAR_PLAN_IMPACT_CURVE_OPTIONS
    : undefined;
  const polygonProgress = isInstant ? 1 : reveal.polygonProgress;

  const previousPath =
    previous != null
      ? buildRadarCurvePath(
          buildRadarDataVertices(previous, maxR, cx, cy, polygonProgress, axes),
          cx,
          cy,
          curveOptions,
        )
      : null;

  const dataVertices = buildRadarDataVertices(
    scores,
    maxR,
    cx,
    cy,
    polygonProgress,
    axes,
  );
  const dataPath = buildRadarCurvePath(dataVertices, cx, cy, curveOptions);

  const displayTotal = isInstant ? total : reveal.displayTotal;
  const baseOpacity = isInstant ? 1 : reveal.baseOpacity;
  const axisLabelOpacity = isInstant ? 1 : reveal.axisLabelOpacity;

  const containerClass = isChip
    ? "size-8"
    : isPlanImpact
      ? "h-[114px] w-[142px]"
      : isCompact
        ? "size-[100px]"
        : "h-[262px] w-[305px]";

  const badgeW = isPlanImpact ? 28 : isCompact ? 28 : 38;
  const badgeH = isPlanImpact ? 20 : isCompact ? 20 : 26;
  const badgeCenterY = cy - RADAR_CENTER_BADGE_GAP - badgeH / 2;
  const badgeTextBaselineOffset = isPlanImpact || isCompact ? 4 : 5;
  const dataPointRadius = isChip ? 1.5 : isPlanImpact ? 2 : 3.5;
  const centerDotRadius = 3;
  const ringStrokeWidth = isChip ? 0.3 : 0.5;
  const dataStrokeWidth = isChip ? 1 : 1.5;

  return (
    <div
      className={cn(
        "relative mx-auto shrink-0 overflow-visible",
        containerClass,
        className,
      )}
    >
      <svg
        viewBox={viewBox}
        className="size-full overflow-visible"
        data-testid="fitness-radar-chart"
        data-size={size}
        role={isChip ? undefined : "img"}
        aria-hidden={isChip || undefined}
        aria-label={
          isChip
            ? undefined
            : isPlanImpact
              ? "Financial fitness score impact illustration"
              : `Financial fitness score: ${total}`
        }
      >
        <g style={{ opacity: baseOpacity }}>
          {RADAR_LEVELS.map((level) => {
            const r = (level / 100) * maxR;
            return (
              <circle
                key={`ring-${level}`}
                cx={cx}
                cy={cy}
                r={r}
                className="fill-background_auralv1_overlay5 stroke-border_auralv1_transparent50"
                strokeWidth={ringStrokeWidth}
              />
            );
          })}

          {showScaleLabels &&
            RADAR_LEVELS.map((level) => {
              const r = (level / 100) * maxR;
              const [x, y] = polarToCartesian(cx, cy, -90, r);
              return (
                <text
                  key={`scale-${level}`}
                  x={x + 6}
                  y={y + 4}
                  className="fill-content_quaternary"
                  fontSize={8}
                  fontFamily="General Sans"
                >
                  {level}
                </text>
              );
            })}

          {axes.map((axis) => {
            const [ex, ey] = polarToCartesian(cx, cy, axis.angle, maxR);
            return (
              <line
                key={`axis-${axis.code}`}
                x1={cx}
                y1={cy}
                x2={ex}
                y2={ey}
                className="stroke-border_auralv1_transparent50"
                strokeWidth={ringStrokeWidth}
              />
            );
          })}
        </g>

        {/* 旧值虚线层（先画，当前实线叠在上）—— Figma FitnessScoreCard 新旧对比 */}
        {previousPath != null ? (
          <path
            d={previousPath}
            className="fill-none stroke-content_brand_primary"
            strokeOpacity={0.4}
            strokeWidth={dataStrokeWidth}
            strokeLinejoin="round"
            strokeDasharray="4 3"
            data-testid="fitness-radar-previous-shape"
          />
        ) : null}

        <path
          d={dataPath}
          className="fill-background_brand_overlay10 stroke-content_brand_primary"
          fillOpacity={isChip ? 0.22 : undefined}
          strokeOpacity={isChip ? 0.95 : undefined}
          strokeWidth={dataStrokeWidth}
          strokeLinejoin="round"
          data-testid="fitness-radar-data-shape"
        />

        {showDataPoints &&
          axes.map((axis) => {
            const value = scoreMap[axis.code];
            const pointR =
              (value / 100) * maxR * (isInstant ? 1 : reveal.polygonProgress);
            const [x, y] = polarToCartesian(cx, cy, axis.angle, pointR);
            return (
              <circle
                key={`point-${axis.code}`}
                cx={x}
                cy={y}
                r={
                  isChip || isPlanImpact || reveal.showDataPoints
                    ? dataPointRadius
                    : 0
                }
                className={
                  isChip
                    ? "fill-background_solid_neutral_primary stroke-content_brand_primary"
                    : "fill-content_brand_primary stroke-border_white_solid"
                }
                strokeWidth={isChip ? 0.5 : 1}
              />
            );
          })}

        {showAxisLabels &&
          axes.map((axis) => {
            const value = scoreMap[axis.code];
            const label = labelPositions[axis.code];
            return (
              <g
                key={`label-${axis.code}`}
                style={{ opacity: axisLabelOpacity }}
              >
                {isPlanImpact ? (
                  <text
                    x={label.x}
                    y={label.y}
                    textAnchor={label.anchor}
                    className="body_medium_xxs fill-content_quaternary"
                  >
                    {DIMENSION_LABELS[axis.code]}
                  </text>
                ) : (
                  <>
                    <text
                      x={label.x}
                      y={label.y}
                      textAnchor={label.anchor}
                      className="body_medium_s fill-content_quaternary"
                    >
                      {DIMENSION_LABELS[axis.code]}
                    </text>
                    <text
                      x={label.x}
                      y={label.y + 16}
                      textAnchor={label.anchor}
                      className="body_bold_s fill-content_accent4_primary"
                    >
                      {value}
                    </text>
                  </>
                )}
              </g>
            );
          })}

        {showCenterDot ? (
          <>
            <circle
              cx={cx}
              cy={cy}
              r={centerDotRadius}
              className="fill-content_accent4_primary"
              data-testid="fitness-radar-center-dot"
            />
            {showCenterScoreBadge ? (
              <>
                <rect
                  x={cx - badgeW / 2}
                  y={badgeCenterY - badgeH / 2}
                  width={badgeW}
                  height={badgeH}
                  rx={6}
                  className="fill-content_primary"
                  data-testid="fitness-radar-center-badge"
                />
                <text
                  x={cx}
                  y={badgeCenterY + badgeTextBaselineOffset}
                  textAnchor="middle"
                  className="body_bold_m fill-content_inverse_primary tabular-nums"
                >
                  {displayTotal}
                </text>
              </>
            ) : null}
          </>
        ) : null}
      </svg>
    </div>
  );
};
